/**
 * The one explicit action on a dish row in the chef's view.
 *
 * "Inizia" appears only once the waiter has actually sent the wave in. Until
 * then the dish says "In attesa": it is not the kitchen's to start, and a
 * button that starts it anyway produces food nobody asked for yet.
 */
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "../i18n";

interface DishRowStatusButtonProps {
  item: any;
  isLoading: boolean;
  label: string;
  onAdvance: () => void;
}

const PENDING_STATUSES = new Set(["PENDING", "CONFIRMED"]);
// i18n-keep: order item status codes compared with the payload, never shown
const COOKING_STATUSES = new Set(["PREPARING", "IN_PROGRESS"]);

export default function DishRowStatusButton({
  item,
  isLoading,
  label,
  onAdvance,
}: DishRowStatusButtonProps) {
  const { t } = useTranslation();
  if (PENDING_STATUSES.has(item.status) && item.firedAt) {
    return (
      <button
        onClick={onAdvance}
        disabled={isLoading}
        className="px-5 py-3 rounded-lg bg-kds-card text-kds-text text-kds-action min-h-touch hover:bg-kds-card/70 disabled:opacity-50 whitespace-nowrap"
        title={t("kds.dishRow.startTitle", "Inizia la preparazione di questo piatto")}
      >
        {isLoading ? "…" : t("kds.dishRow.start", "▶ Inizia")}
      </button>
    );
  }

  if (PENDING_STATUSES.has(item.status)) {
    return (
      <span className="px-5 py-3 rounded-lg bg-kds-card/30 text-kds-muted text-kds-meta whitespace-nowrap italic">
        {t("kds.dishRow.waiting", "In attesa")}
      </span>
    );
  }

  if (COOKING_STATUSES.has(item.status)) {
    return (
      <button
        onClick={onAdvance}
        disabled={isLoading}
        className="px-6 py-3 rounded-lg bg-kds-preparing text-black text-kds-action min-h-touch hover:brightness-110 disabled:opacity-50 whitespace-nowrap animate-preparing"
        title={t("kds.dishRow.readyTitle", "Segna come pronto — il cameriere sarà notificato")}
      >
        {isLoading ? "…" : t("kds.dishRow.ready", "🔔 PRONTO")}
      </button>
    );
  }

  if (item.status === "READY") {
    return (
      <span className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-kds-ready/20 text-kds-ready text-kds-meta whitespace-nowrap">
        <CheckCircle2 size={22} /> {t("kds.dishRow.readyNotified", "Pronto • cameriere avvisato")}
      </span>
    );
  }

  if (item.status === "SERVED") {
    return (
      <span className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-kds-served/20 text-kds-muted text-kds-meta whitespace-nowrap">
        {t("kds.status.served", "Servito")}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-kds-cancelled/20 text-kds-cancelled text-kds-meta whitespace-nowrap">
      {label}
    </span>
  );
}
